import { natReadiness, type ConnectionState, type NatProfile } from '@connaro/shared';
import { ApiError, api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';
import { EmptyState, ErrorState, Loading } from './states';

/** The NAT profile as the connection endpoints return it. */
interface NatProfileResponse extends NatProfile {
  reboundCount: number;
  observedAt: string;
}

/** The chip style for each connection state (see styles.css chip modifiers). */
const CHIP_CLASS: Record<ConnectionState, string> = {
  DIRECT: 'chip--success',
  NAT_TRAVERSED: 'chip--success',
  RELAYED: 'chip--warning',
  DEGRADED: 'chip--degraded',
  FAILED: 'chip--failed',
};

/**
 * A connection-state chip for the five canonical states.
 *
 * The label and colour come only from the state, so the same badge is used
 * wherever a connection is shown — a device's expected path here, a live tunnel's
 * observed state later — and always reads the same.
 */
export function ConnectionStateChip({ state }: { state: ConnectionState }): JSX.Element {
  const t = useT();
  return (
    <span className={`chip ${CHIP_CLASS[state]}`} data-state={state}>
      {t(`connection.${state}`)}
    </span>
  );
}

/**
 * How one edge is able to connect, from its own NAT profile.
 *
 * The state shown is the edge's *best case* — the ceiling a peer would have to
 * meet — not a live connection, because a real path depends on both peers and no
 * tunnel is observed here. It is labelled as the expected path so an operator does
 * not read it as a running state; the degraded and failed states belong to an
 * observed tunnel and are never shown from a profile alone.
 */
export function ConnectivityPanel({ deviceId }: { deviceId: string }): JSX.Element {
  const t = useT();
  const { data, error, loading, reload } = useAsync(
    () => api.get<NatProfileResponse>(`/nat/devices/${deviceId}/profile`),
    [deviceId],
  );

  if (loading) return <Loading />;

  // A device that has not reported a profile is the common case, not an error, so
  // a 404 shows the empty hint rather than a red error state.
  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div className="card">
          <h2>{t('connection.title')}</h2>
          <EmptyState hint={t('connection.noProfile')} />
        </div>
      );
    }
    return <ErrorState message={error.message} onRetry={reload} />;
  }
  if (!data) return <Loading />;

  const state = natReadiness(data);

  return (
    <div className="card">
      <div className="page-head">
        <h2>{t('connection.title')}</h2>
        <ConnectionStateChip state={state} />
      </div>
      {/* The chip above is the expected path; the reason and facts below say why. */}
      <p className="muted">{data.reason}</p>
      <dl className="kv">
        <dt>{t('connection.mapping')}</dt>
        <dd>{data.mapping}</dd>
        <dt>{t('connection.holePunchable')}</dt>
        <dd>{data.holePunchable ? '✓' : '—'}</dd>
        <dt>{t('connection.cgnat')}</dt>
        <dd>{data.cgnat ? '✓' : '—'}</dd>
      </dl>
    </div>
  );
}
